export type OfflineKind = 'CREATE_ENTRY' | 'PREPARE_PASS' | 'CONFIRM_QR_EXIT' | 'CONFIRM_VISIT_EXIT' | 'REPORT_OCCUPANCY' | 'REPORT_INCIDENT'

export type OfflineOperation = {
  id: string
  kind: OfflineKind
  createdAt: string
  attempts: number
  lastError?: string
  payload: Record<string, unknown>
}

const DB_NAME = 'gatepass-pwa'
const DB_VERSION = 1
const OPERATIONS = 'operations'
const VISITS = 'visits'

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(OPERATIONS)) db.createObjectStore(OPERATIONS, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(VISITS)) db.createObjectStore(VISITS, { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function queueOperation(kind: OfflineKind, payload: Record<string, unknown>) {
  const db = await openDb()
  const operation: OfflineOperation = {
    id: crypto.randomUUID(),
    kind,
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0,
  }
  await requestResult(db.transaction(OPERATIONS, 'readwrite').objectStore(OPERATIONS).put(operation))
  db.close()
  return operation
}

export async function getOperations(): Promise<OfflineOperation[]> {
  const db = await openDb()
  const rows = await requestResult(db.transaction(OPERATIONS, 'readonly').objectStore(OPERATIONS).getAll())
  db.close()
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function removeOperation(id: string) {
  const db = await openDb()
  await requestResult(db.transaction(OPERATIONS, 'readwrite').objectStore(OPERATIONS).delete(id))
  db.close()
}

export async function markOperationFailed(operation: OfflineOperation, error: string) {
  const db = await openDb()
  await requestResult(db.transaction(OPERATIONS, 'readwrite').objectStore(OPERATIONS).put({
    ...operation,
    attempts: operation.attempts + 1,
    lastError: error,
  }))
  db.close()
}

export async function cacheVisits(visits: import('../types').Visit[]) {
  const db = await openDb()
  const tx = db.transaction(VISITS, 'readwrite')
  tx.objectStore(VISITS).clear()
  visits.forEach((visit) => tx.objectStore(VISITS).put(visit))
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}

export async function getCachedVisits(): Promise<import('../types').Visit[]> {
  const db = await openDb()
  const visits = await requestResult(db.transaction(VISITS, 'readonly').objectStore(VISITS).getAll())
  db.close()
  return visits
}
